 

import { useState, useEffect } from "react"
import { motion, AnimatePresence } from "framer-motion"
import "../styles/QueueVisualizer.css"
import Button from "../../shared/Button";

const PERSON_COLORS = ["#FF6B6B", "#4ECDC4", "#FFD166", "#06D6A0", "#118AB2", "#EF476F", "#FFC43D", "#1B9AAA"]

function QueueVisualizer({ addLog, currentStep }) {
  const [queue, setQueue] = useState([])
  const [inputValue, setInputValue] = useState("")
  const [isAnimating, setIsAnimating] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(null)
  const [showTooltip, setShowTooltip] = useState(false)
  const [tooltipContent, setTooltipContent] = useState("")
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 })
  const [showPopup, setShowPopup] = useState(false)
  const [popupMessage, setPopupMessage] = useState("")

  // For guided demo
  useEffect(() => {
    if (currentStep === null) return

    // Reset queue for demo
    if (currentStep === 0) {
      setQueue([])
      addLog("Initialized empty queue", "system")
    } else if (currentStep === 1) {
      // First enqueue
      const demoItem = {
        value: "First",
        id: "demo-1",
        color: PERSON_COLORS[0],
      }
      setQueue([demoItem])
      addLog("Enqueued 'First' to the queue", "system")
    } else if (currentStep === 2) {
      // Second enqueue
      const demoItems = [
        {
          value: "First",
          id: "demo-1",
          color: PERSON_COLORS[0],
        },
        {
          value: "Second",
          id: "demo-2",
          color: PERSON_COLORS[1],
        },
      ]
      setQueue(demoItems)
      addLog("Enqueued 'Second' to the queue", "system")
    } else if (currentStep === 3) {
      // Dequeue operation
      const demoItems = [
        {
          value: "Second",
          id: "demo-2",
          color: PERSON_COLORS[1],
        },
      ]
      setQueue(demoItems)
      addLog("Dequeued 'First' from the queue", "system")
    } else if (currentStep === 4) {
      // Final state
      const demoItems = [
        {
          value: "Second",
          id: "demo-2",
          color: PERSON_COLORS[1],
        },
      ]
      setQueue(demoItems)
      addLog("Current queue state shown", "system")
    }
  }, [currentStep, addLog])

  const handleEnqueue = () => {
    if (inputValue.trim() === "") return
    if (queue.length >= 8) {
      addLog("⚠️ Queue is full! Cannot add more people.", "error")
      setPopupMessage("No more empty space! Queue is full.")
      setShowPopup(true)
      setTimeout(() => setShowPopup(false), 3000)
      return
    }

    setIsAnimating(true)

    // Generate a random color for the new person
    const randomColor = PERSON_COLORS[Math.floor(Math.random() * PERSON_COLORS.length)]

    const newItem = {
      value: inputValue,
      id: Date.now(),
      color: randomColor,
    }

    addLog(`Enqueuing "${inputValue}" to the queue`, "success")
    setQueue((prevQueue) => [...prevQueue, newItem])
    setInputValue("")

    setTimeout(() => {
      setIsAnimating(false)
    }, 500)
  }

  const handleDequeue = () => {
    if (queue.length === 0) {
      addLog("⚠️ Queue is empty! Cannot remove from an empty queue.", "error")
      return
    }

    setIsAnimating(true)
    addLog(`Dequeuing "${queue[0].value}" from the queue`, "info")

    setTimeout(() => {
      setQueue((prevQueue) => prevQueue.slice(1))
      setIsAnimating(false)
    }, 500)
  }

  const handleClear = () => {
    setQueue([])
    addLog("Queue cleared", "warning")
  }

  const handleMouseEnter = (index, item) => {
    setHighlightedIndex(index)
    setShowTooltip(true)
    setTooltipContent(`Value: ${item.value}, Position: ${index + 1}`)
  }

  const handleMouseMove = (e) => {
    setTooltipPosition({ x: e.clientX, y: e.clientY })
  }

  const handleMouseLeave = () => {
    setHighlightedIndex(null)
    setShowTooltip(false)
  }

  return (
    <div className="queue-container">
      <div className="queue-controls">
        <div className="input-group">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="Enter a name"
            disabled={isAnimating || currentStep !== null}
            className="queue-input"
            onKeyDown={(e) => { if (e.key === "Enter") handleEnqueue() }}
          />
          <div className="queue-buttons">
            <Button onClick={handleEnqueue} disabled={isAnimating || inputValue.trim() === "" || currentStep !== null}>Enqueue</Button>
            <Button variant="secondary" onClick={handleDequeue} disabled={isAnimating || queue.length === 0 || currentStep !== null}>Dequeue</Button>
            <Button variant="danger" onClick={handleClear} disabled={isAnimating || queue.length === 0 || currentStep !== null}>Clear</Button>
          </div>
        </div>
      </div>

      <div className="queue-visualization">
        <div className="queue-labels">
          <div className="queue-front-label">Front</div>
          <div className="queue-rear-label">Rear</div>
        </div>

        <div className="coffee-shop-scene">
          <div className="coffee-shop">
            <div className="coffee-shop-sign">
              <span>COFFEE</span>
            </div>
            <div className="coffee-counter">
              <div className="coffee-machine">
                <div className="machine-top"></div>
                <div className="machine-body"></div>
              </div>
              <div className="barista">
                <div className="barista-head"></div>
                <div className="barista-body"></div>
                <div className="barista-arm"></div>
              </div>
            </div>
          </div>

          <div className="queue-line">
            <div className="floor-tiles"></div>

            <AnimatePresence>
              {queue.map((item, index) => (
                <motion.div
                  key={item.id}
                  className={`person ${highlightedIndex === index ? "highlighted" : ""}`}
                  style={{ backgroundColor: item.color }}
                  initial={{ x: 100, opacity: 0 }}
                  animate={{
                    x: 0,
                    opacity: 1,
                    transition: {
                      type: "spring",
                      stiffness: 200,
                      damping: 20,
                    },
                  }}
                  exit={{
                    x: -100,
                    opacity: 0,
                    transition: { duration: 0.3 },
                  }}
                  transition={{ duration: 0.5 }}
                  onMouseEnter={() => handleMouseEnter(index, item)}
                  onMouseMove={handleMouseMove}
                  onMouseLeave={handleMouseLeave}
                  whileHover={{ scale: 1.1, y: -5 }}
                >
                  <div className="person-head"></div>
                  <div className="person-body"></div>
                  <div className="person-legs">
                    <div className="person-leg left"></div>
                    <div className="person-leg right"></div>
                  </div>
                  <div className="person-name">{item.value}</div>
                  {index === 0 && (
                    <div className="person-bubble">
                      <div className="bubble-text">Next please!</div>
                    </div>
                  )}
                </motion.div>
              ))}
            </AnimatePresence>

            {/* Static people waiting in line (always visible) */}
            <div className="waiting-people">
              <div className="static-person" style={{ backgroundColor: "#FFD166" }}>
                <div className="person-head"></div>
                <div className="person-body"></div>
                <div className="person-legs">
                  <div className="person-leg left"></div>
                  <div className="person-leg right"></div>
                </div>
                <div className="person-phone">
                  <div className="phone-screen"></div>
                </div>
              </div>
              <div className="static-person" style={{ backgroundColor: "#06D6A0" }}>
                <div className="person-head"></div>
                <div className="person-body"></div>
                <div className="person-legs">
                  <div className="person-leg left"></div>
                  <div className="person-leg right"></div>
                </div>
                <div className="person-bubble small">
                  <div className="bubble-text">So tired...</div>
                </div>
              </div>
              <div className="static-person" style={{ backgroundColor: "#118AB2" }}>
                <div className="person-head"></div>
                <div className="person-body"></div>
                <div className="person-legs">
                  <div className="person-leg left"></div>
                  <div className="person-leg right"></div>
                </div>
                <div className="person-coffee">
                  <div className="coffee-cup"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {showTooltip && (
          <div
            className="tooltip"
            style={{
              left: tooltipPosition.x + 10,
              top: tooltipPosition.y + 10,
            }}
          >
            {tooltipContent}
          </div>
        )}

        {showPopup && (
          <motion.div
            className="popup-message"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
          >
            {popupMessage}
          </motion.div>
        )}
      </div>

      <div className="queue-info">
        <div className="queue-status">
          <div className="status-item">
            <span className="status-label">Size:</span>
            <span className="status-value">{queue.length}</span>
          </div>
          <div className="status-item">
            <span className="status-label">Is Empty:</span>
            <span className="status-value">{queue.length === 0 ? "Yes" : "No"}</span>
          </div>
          <div className="status-item">
            <span className="status-label">Is Full:</span>
            <span className="status-value">{queue.length >= 8 ? "Yes" : "No"}</span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default QueueVisualizer
